import React from 'react';
import { ChevronDown, ChevronUp, ListPlus, Plus, Trash2 } from 'lucide-react';

const UNIT_OPTIONS = [
  { value: 'pz', label: 'pz' },
  { value: 'hr', label: 'hr' },
  { value: 'spot', label: 'spot' },
  { value: 'version', label: 'versión' },
  { value: 'mes', label: 'mes' },
  { value: 'proyecto', label: 'proyecto' },
  { value: 'sesion', label: 'sesión' },
];

export function HandFreeSection({ isOpen, onToggle, rows, onRowsChange, formatPrice, currency }) {
  const addRow = () => {
    onRowsChange([...rows, { id: Date.now(), description: '', unit: 'pz', unitPrice: 0, quantity: 1 }]);
  };

  const updateRow = (id, field, value) => {
    onRowsChange(rows.map(r => r.id === id ? { ...r, [field]: value } : r));
  };

  const removeRow = (id) => {
    onRowsChange(rows.filter(r => r.id !== id));
  };

  const total = rows.reduce((sum, r) => sum + (r.unitPrice || 0) * (r.quantity || 0), 0);

  return (
    <div className="border border-white/10 rounded-xl overflow-hidden bg-gradient-to-b from-[#1a1f50]/50 to-[#080c2d]/50" data-testid="hand-free-section">
      <button onClick={onToggle} className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors" data-testid="btn-toggle-hand-free">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
            <ListPlus className="w-5 h-5 text-white" />
          </div>
          <div className="text-left">
            <span className="text-white font-semibold text-lg block">Hand-Free</span>
            {rows.length > 0 && total > 0 && (
              <span className="text-xs font-bold text-green-400">{rows.filter(r => r.unitPrice > 0).length} item{rows.filter(r => r.unitPrice > 0).length !== 1 ? 's' : ''} — Total: {formatPrice(total)} {currency}</span>
            )}
          </div>
        </div>
        {isOpen ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
      </button>
      {isOpen && (
        <div className="p-4 pt-0 space-y-3">
          {/* Header labels */}
          {rows.length > 0 && (
            <div className="grid grid-cols-[1fr_80px_90px_60px_30px] gap-2 text-xs text-slate-500 font-medium px-1">
              <span>Descripción</span>
              <span>Unidad</span>
              <span>Precio/u</span>
              <span>Cant.</span>
              <span></span>
            </div>
          )}
          {rows.map((row) => {
            const lineTotal = (row.unitPrice || 0) * (row.quantity || 0);
            return (
              <div key={row.id} className={`grid grid-cols-[1fr_80px_90px_60px_30px] gap-2 items-center p-2 rounded-lg border transition-all ${lineTotal > 0 ? 'border-[#aa2aff]/30 bg-[#aa2aff]/5' : 'border-white/5 bg-white/[0.02]'}`}>
                <input
                  type="text"
                  value={row.description}
                  onChange={(e) => updateRow(row.id, 'description', e.target.value)}
                  placeholder="Servicio..."
                  className="bg-transparent border-b border-white/10 text-white text-sm px-1 py-1 focus:border-[#aa2aff] focus:outline-none placeholder-slate-600"
                  data-testid={`hf-desc-${row.id}`}
                />
                <select
                  value={row.unit}
                  onChange={(e) => updateRow(row.id, 'unit', e.target.value)}
                  className="bg-[#080c2d] border border-white/10 text-white text-xs rounded px-1 py-1 focus:border-[#aa2aff] focus:outline-none"
                  data-testid={`hf-unit-${row.id}`}
                >
                  {UNIT_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <div className="relative">
                  <span className="absolute left-1 top-1/2 -translate-y-1/2 text-slate-500 text-xs">$</span>
                  <input
                    type="number"
                    value={row.unitPrice || ''}
                    onChange={(e) => updateRow(row.id, 'unitPrice', parseFloat(e.target.value) || 0)}
                    placeholder="0"
                    className="bg-[#080c2d] border border-white/10 text-white text-xs rounded pl-4 pr-1 py-1 w-full focus:border-[#aa2aff] focus:outline-none"
                    data-testid={`hf-price-${row.id}`}
                  />
                </div>
                <input
                  type="number"
                  min="0"
                  value={row.quantity || ''}
                  onChange={(e) => updateRow(row.id, 'quantity', parseInt(e.target.value) || 0)}
                  className="bg-[#080c2d] border border-white/10 text-white text-xs rounded px-1 py-1 w-full text-center focus:border-[#aa2aff] focus:outline-none"
                  data-testid={`hf-qty-${row.id}`}
                />
                <button onClick={() => removeRow(row.id)} className="w-6 h-6 rounded hover:bg-red-500/20 flex items-center justify-center text-slate-500 hover:text-red-400 transition-colors" data-testid={`btn-remove-hf-row-${row.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
                {lineTotal > 0 && (
                  <p className="col-span-5 text-xs text-green-400 font-bold pl-1 -mt-1">
                    = {formatPrice(lineTotal)} {currency}
                  </p>
                )}
              </div>
            );
          })}
          <button
            onClick={addRow}
            className="w-full p-3 rounded-lg border border-dashed border-white/20 hover:border-[#aa2aff]/50 hover:bg-[#aa2aff]/5 flex items-center justify-center gap-2 text-slate-400 hover:text-white transition-all text-sm"
            data-testid="btn-add-hf-row"
          >
            <Plus className="w-4 h-4" /> Agregar línea
          </button>
          {total > 0 && (
            <div className="flex justify-between items-center p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
              <span className="text-white font-semibold text-sm">Subtotal Hand-Free</span>
              <span className="text-emerald-400 font-bold text-lg">{formatPrice(total)} {currency}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default HandFreeSection;
